import type { Address } from "viem"
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx"

import type { IStorageBackend } from "@/domains/common/storage"
import type { IEventBus } from "@/domains/events"
import type { LockOrchestrator } from "@/domains/lock/lock-orchestrator"
import type { LockRepository } from "@/domains/lock/lock-repository"
import type { WalletConnectionStore } from "@/domains/wallet"
import { LcAddress } from "@/domains/core"

import type { ManagerApprovalOrchestrator } from "./manager-approval-orchestrator"
import { ManagerModeChangedEvent } from "./events"

export class ManagerStore {
  managerAddresses: LcAddress[] = []
  selectedManagerAddress: LcAddress | null = null
  managerApprovalStatus = new Map<LcAddress, boolean>()
  managerAddressNames = new Map<LcAddress, string>()

  constructor(
    private readonly storage: IStorageBackend,
    private readonly chainSlug: string,
    private readonly walletStore: WalletConnectionStore,
    private readonly lockRepository: LockRepository,
    private lockOrchestrator: LockOrchestrator,
    private readonly eventBus: IEventBus,
    private approvalOrchestrator?: ManagerApprovalOrchestrator,
  ) {
    makeObservable(this, {
      managerAddresses: observable,
      selectedManagerAddress: observable,
      managerApprovalStatus: observable,
      managerAddressNames: observable,
      effectiveAddress: computed,
      isManagerMode: computed,
      addManagerAddress: action,
      removeManagerAddress: action,
      setSelectedManagerAddress: action,
      setManagerApprovalStatus: action,
      setManagerAddressName: action,
    })

    // React to selectedManagerAddress changes to restart orchestrator queries
    reaction(
      () => this.selectedManagerAddress,
      () => {
        this.handleAddressChange()
      },
    )
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()
    })
  }

  private initializeFromStorage(): void {
    // Load manager addresses and names from localStorage
    const storedAddresses = this.storage.getItem(`${this.chainSlug}-manager-addresses`)
    if (storedAddresses) {
      try {
        const parsed = JSON.parse(storedAddresses)
        // Support both old format (array of addresses) and new format (array of {address, name})
        if (Array.isArray(parsed)) {
          if (parsed.length > 0 && typeof parsed[0] === "string") {
            // Old format: array of addresses (strings) - convert to LcAddress
            this.managerAddresses = parsed.map((addr: string) => new LcAddress(addr))
          } else {
            // New format: array of {address, name} - convert addresses to LcAddress
            const addressData = parsed as Array<{ address: string; name?: string }>
            this.managerAddresses = addressData.map((item) => new LcAddress(item.address))
            addressData.forEach((item, index) => {
              const address = this.managerAddresses[index]
              if (item.name && address) {
                this.managerAddressNames.set(address, item.name)
              }
            })
          }
        }
      } catch {
        // Invalid JSON, ignore
      }
    }

    // Load selected manager address from localStorage
    const storedSelected = this.storage.getItem(`${this.chainSlug}-manager-selected-address`)
    if (storedSelected) {
      try {
        const parsedAddress = JSON.parse(storedSelected) as string
        if (parsedAddress) {
          const selectedLcAddress = new LcAddress(parsedAddress)
          // Check if the selected address exists in managerAddresses using equals()
          if (this.managerAddresses.some((addr) => addr.equals(selectedLcAddress))) {
            this.selectedManagerAddress = selectedLcAddress
          }
        }
      } catch {
        // Invalid JSON or invalid address, ignore
      }
    }
  }

  get effectiveAddress(): Address | null {
    // If a manager address is selected, use it (convert to viem Address)
    if (this.selectedManagerAddress) {
      return this.selectedManagerAddress.toViemAddress()
    }

    // Otherwise, fall back to connected wallet address
    return this.walletStore.connectedAddress?.toViemAddress() ?? null
  }

  get isManagerMode(): boolean {
    // Manager mode is active when a manager address is selected
    return this.selectedManagerAddress !== null
  }

  addManagerAddress(address: LcAddress, name?: string): void {
    if (this.managerAddresses.some((addr) => addr.equals(address))) {
      return
    }

    this.managerAddresses.push(address)
    if (name) {
      this.managerAddressNames.set(address, name)
    }
    this.persistManagerAddresses()
    // Trigger immediate approval check for the new address
    if (this.approvalOrchestrator?.query) {
      this.approvalOrchestrator.query.forceExecute()
    }
  }

  removeManagerAddress(address: LcAddress): void {
    this.managerAddresses = this.managerAddresses.filter((addr) => !addr.equals(address))
    this.managerAddressNames.delete(address)

    // If the removed address was selected, clear selection
    if (this.selectedManagerAddress?.equals(address)) {
      this.setSelectedManagerAddress(null)
    }

    this.persistManagerAddresses()
  }

  setSelectedManagerAddress(address: LcAddress | null): void {
    // If setting an address, validate it's in the manager addresses list
    if (address && !this.managerAddresses.some((addr) => addr.equals(address))) {
      console.warn(`Cannot select manager address ${address.toString()} - not in manager addresses list`)
      return
    }

    this.selectedManagerAddress = address
    this.persistSelectedManager()
  }

  setLockOrchestrator(orchestrator: LockOrchestrator): void {
    this.lockOrchestrator = orchestrator
  }

  setApprovalOrchestrator(orchestrator: ManagerApprovalOrchestrator): void {
    this.approvalOrchestrator = orchestrator
  }

  setManagerApprovalStatus(address: LcAddress, isApproved: boolean): void {
    this.managerApprovalStatus.set(address, isApproved)
  }

  getManagerApprovalStatus(address: LcAddress): boolean {
    return this.managerApprovalStatus.get(address) ?? false
  }

  setManagerAddressName(address: LcAddress, name: string): void {
    if (!this.managerAddresses.some((addr) => addr.equals(address))) {
      console.warn(`Cannot set name for ${address.toString()} - not in manager addresses list`)
      return
    }
    this.managerAddressNames.set(address, name)
    this.persistManagerAddresses()
  }

  getManagerAddressName(address: LcAddress): string | undefined {
    return this.managerAddressNames.get(address)
  }

  private persistManagerAddresses(): void {
    // Store as array of {address, name} objects
    // LcAddress.toJSON() automatically serializes to string
    const addressData = this.managerAddresses.map((address) => ({
      address: address.toJSON(),
      name: this.managerAddressNames.get(address),
    }))
    this.storage.setItem(`${this.chainSlug}-manager-addresses`, JSON.stringify(addressData))
  }

  private persistSelectedManager(): void {
    if (this.selectedManagerAddress) {
      // LcAddress.toJSON() automatically serializes to string
      this.storage.setItem(
        `${this.chainSlug}-manager-selected-address`,
        JSON.stringify(this.selectedManagerAddress.toJSON()),
      )
    } else {
      this.storage.setItem(`${this.chainSlug}-manager-selected-address`, "")
    }
  }

  private handleAddressChange(): void {
    // Stop current orchestrator queries
    if (this.lockOrchestrator.query) {
      this.lockOrchestrator.query.stop()
    }

    // Clear the manager's repository
    this.lockRepository.clearAll()

    // Publish manager mode changed event
    const isManagerMode = this.isManagerMode
    this.eventBus.publish(new ManagerModeChangedEvent(isManagerMode, this.selectedManagerAddress))

    // Only restart queries if we're actually in manager mode
    // When exiting manager mode, queries should stay stopped
    if (this.isManagerMode && this.lockOrchestrator.query) {
      const effectiveAddr = this.effectiveAddress
      if (effectiveAddr) {
        this.lockOrchestrator.query.start()
      }
    }
  }
}
